/**
 * Foundations Field Mapping
 * Maps field names to their display labels and units
 */

import { EVALUATE_CODES, EvaluateCode } from './constants';
import { formatDimensionLabel } from './dimensionLabel';

export const FOUNDATIONS_FIELD_LABELS: { [fieldName: string]: string } = {
  // Excavate & Concrete
  'pileMat': 'xxxmm thick recycled 6F2 piling mat',
  'pileMatRemoval': 'Excavate to remove pile mat & re-use',
  'plotGarSetOut': 'Engineer setting out houses & garages',
  'pileSetOut': 'Setting out for piling contractor',
  'pileAttendance': 'Attendance upon piling contractor',
  'excavateTrench': 'Excavate trench and tip on site',
  'excavatePiled': 'Excavate trench piled foundation and tip on site',
  'excavateVibro': 'Excavate vibro foundation and tip on site',
  'levelCompact': 'Consolidate bottoms of trenches',
  'stockpileArisings': 'Deposit on site (arisings)',
  'concreteBlinding': 'xxmm thick concrete blinding',
  'formplasOrSimilar': 'Formwork to groundbeams (Formplas or similar)',
  'cropPiles': 'Pile crop',
  'fillSteelPiles': 'Filling Steel Piles',
  'rebar': 'Steel Reinforcement (bar)',
  'stepsInFounds': 'EO step at party wall',
  'meshReinforcement': 'Mesh Reinforcement',
  'gen1ConcreteToStrip': 'xx Concrete to Strip foundation',
  'rc35ConcreteToPiled': 'xx Concrete to Piled foundation',
  'rc35ConcreteToVibro': 'xx Concrete to Vibro foundation',
  'stripClaymaster': 'Strip Claymaster',
  'piledClaymaster': 'Piled Claymaster',

  // Substructure
  'trench4NBlock300mmWide': 'Trench block 300mm wide 3.6N',
  'trench7NBlock300mmWide': 'Trench block 300mm wide 7.0N',
  'trench4NBlock355mmWide': 'Trench block 355mm wide 3.6N',
  'trench7NBlock355mmWide': 'Trench block 355mm wide 7.0N',
  'dense4NBlockwork100mm': '3.6N 100mm Dense Blockwork',
  'dense7NBlockwork100mm': 'Dense concrete blockwork 100 thk 7n/mm2 (Ext Leaf)',
  'dense10NBlockwork100mm': '10N 100mm Dense Blockwork',
  'aerated4NBlockwork100mm': 'Aerated concrete blockwork 100 thk 3.6n/mm2 (Ext leaf)',
  'aerated7NBlockwork100mm': 'Aerated concrete blockwork 100 thk 7n/mm2 (Ext leaf)',
  'aerated4NBlockwork140mm': 'Aerated concrete blockwork 140 thk 3.6n/mm2',
  'aerated7NBlockwork140mm': 'Aerated concrete blockwork 140 thk 7n/mm2',
  'dense100mmCoursingBrick': 'Dense HB Common Brickwork - 100mm',
  'aerated100mmCoursingBrick': 'Aerated HB Common Brickwork - 100mm',
  'aerated140mmCoursingBrick': 'Aerated HB Common Brickwork - 140mm',
  'layFacingBrickwork': 'HB wall in facings FREE ISSUE - 215 x 102.5 x 65mm',
  'supplyFacingBrickwork': 'HB wall in Red Class B\'s (perf) M&J Supply - 215 x 102.5 x 65mm',
  'beamInfill': 'T Closure (dense 100 wide)',
  'formCavity': 'Form Cavity',
  'cavityFill': 'Cavity infill concrete',
  'telescopicVents': 'Telescopic vents (including lintel) - 100 wide',
  'telescopicVents140': 'Telescopic vents (including lintel) - 140 wide',
  'perimeterInsulation': '25mm Perimeter Strip',
  'perimeterInsulation30': '30mm Perimeter Strip',
  'dpc100mm': 'DPC 100mm wide',
  'backfillExcavations': 'Backfill with as dug',
  'cavityInsulation': 'Cavity insulation',

  // Abnormals
  'additionalExcavation': 'Additional Excavation',
  'gen1AdditionalConcrete': 'xx in additional concrete',
  'additional300mmTrenchBlock': 'Additional 300mm Trench Block',
  'additional355mmTrenchBlock': 'Additional 355mm Trench Block',
  'additional100mmBlockwork': 'Additional 7.2N Aerated 100mm Blockwork',
  'backfillExcavationsAsDug': 'Backfill excavations with as dug',
  'depositOnSite': 'Deposit on site',
  'eoExposedBrickwork': 'EO exposed brickwork',
  'eoTanking': 'EO tanking, inc retaining blockwork',
  'depositCFAArisings': 'Deposit CFA arisings on site',
  'pileCollar': 'Pile Collar',
  'cordekCellcore155': 'Cordek Cellcore 155mm HX-B',
  'cordekCellcore225': 'Cordek Cellcore 225mm HX-B',

  // Floors
  'bbFloorHouse': 'B&B floor House',
  'bbFloorGarage': 'B&B floor Garage',
  'layOnlyFloors': 'Lay only Floors',
  'insituFloorHouse': 'Insitu Floor House',
  'topHats': 'Top Hats',
  'eo2000gDPM': 'EO 2000g DPM',
  'eoAmber1DPM': 'EO Amber 1 DPM',
  'eoAmber2DPM': 'EO AMBER 2 DPM',
  'radonMembrane': 'Radon Membrane',
  'gasMembrane': 'Gas Membrane with validation',
  'meshToFloor': 'Mesh to floor',
  'topLayerMesh': 'Top layer Mesh',
  'insituFloorGarage': 'Cast Insitu Slab - Garages',
  'meshToGarageFloor': 'Mesh to Garage floor',
  'raft': 'Raft',
};

export const FOUNDATIONS_FIELD_UNITS: { [fieldName: string]: string } = {
  // Excavate & Concrete
  'pileMat': 'm²',
  'pileMatRemoval': 'm³',
  'plotGarSetOut': 'Nr',
  'pileSetOut': 'Nr',
  'pileAttendance': 'Nr',
  'excavateTrench': 'm³',
  'excavatePiled': 'm³',
  'excavateVibro': 'm³',
  'levelCompact': 'm²',
  'stockpileArisings': 'm³',
  'concreteBlinding': 'm²',
  'formplasOrSimilar': 'm',
  'cropPiles': 'Nr',
  'fillSteelPiles': 'Nr',
  'rebar': 't',
  'stepsInFounds': 'Nr',
  'meshReinforcement': 'm²',
  'gen1ConcreteToStrip': 'm³',
  'rc35ConcreteToPiled': 'm³',
  'rc35ConcreteToVibro': 'm³',
  'stripClaymaster': 'm²',
  'piledClaymaster': 'm²',

  // Substructure
  'trench4NBlock300mmWide': 'm²',
  'trench7NBlock300mmWide': 'm²',
  'trench4NBlock355mmWide': 'm²',
  'trench7NBlock355mmWide': 'm²',
  'dense4NBlockwork100mm': 'm²',
  'dense7NBlockwork100mm': 'm²',
  'dense10NBlockwork100mm': 'm²',
  'aerated4NBlockwork100mm': 'm²',
  'aerated7NBlockwork100mm': 'm²',
  'aerated4NBlockwork140mm': 'm²',
  'aerated7NBlockwork140mm': 'm²',
  'dense100mmCoursingBrick': 'm²',
  'aerated100mmCoursingBrick': 'm²',
  'aerated140mmCoursingBrick': 'm²',
  'layFacingBrickwork': 'm²',
  'supplyFacingBrickwork': 'm²',
  'beamInfill': 'm',
  'formCavity': 'm²',
  'cavityFill': 'm³',
  'telescopicVents': 'Nr',
  'telescopicVents140': 'Nr',
  'perimeterInsulation': 'm',
  'perimeterInsulation30': 'm',
  'dpc100mm': 'm',
  'backfillExcavations': 'm³',
  'cavityInsulation': 'm',

  // Abnormals
  'additionalExcavation': 'm³',
  'gen1AdditionalConcrete': 'm³',
  'additional300mmTrenchBlock': 'm²',
  'additional355mmTrenchBlock': 'm²',
  'additional100mmBlockwork': 'm²',
  'backfillExcavationsAsDug': 'm³',
  'depositOnSite': 'm³',
  'eoExposedBrickwork': 'm²',
  'eoTanking': 'm²',
  'depositCFAArisings': 'm³',
  'pileCollar': 'Nr',
  'cordekCellcore155': 'm²',
  'cordekCellcore225': 'm²',

  // Floors
  'bbFloorHouse': 'm²',
  'bbFloorGarage': 'm²',
  'layOnlyFloors': 'm²',
  'insituFloorHouse': 'm²',
  'topHats': '',
  'eo2000gDPM': 'm²',
  'eoAmber1DPM': 'm²',
  'eoAmber2DPM': 'm²',
  'radonMembrane': 'm²',
  'gasMembrane': 'm²',
  'meshToFloor': 'm²',
  'topLayerMesh': 'm²',
  'insituFloorGarage': 'm²',
  'meshToGarageFloor': 'm²',
  'raft': 'm²',
};

/**
 * Get the display label for a Foundations field
 * @param fieldName - The field name to get label for
 * @param meshReinforcementType - Optional: Dynamic mesh reinforcement type from tender context
 * @param substructureBlockType - Optional: Dynamic substructure block type from tender context
 * @param prefixValues - Optional: Dynamic prefix values for concrete type fields
 */
export function getFoundationsFieldLabel(
  fieldName: string,
  meshReinforcementType?: string,
  substructureBlockType?: string,
  prefixValues?: Record<string, string>,
): string | null {
  // Handle dynamic mesh reinforcement label
  if (fieldName === 'meshReinforcement' && meshReinforcementType) {
    return `Mesh Reinforcement ${meshReinforcementType}`;
  }

  // Handle dynamic mesh to floor label (stored in foundationsPrefixValues)
  if (fieldName === 'meshToFloor' && prefixValues?.meshToFloor) {
    return `Mesh to Floor ${prefixValues.meshToFloor}`;
  }

  // Handle dynamic top layer mesh label (stored in foundationsPrefixValues)
  if (fieldName === 'topLayerMesh' && prefixValues?.topLayerMesh) {
    return `Top Layer Mesh ${prefixValues.topLayerMesh}`;
  }

  // Handle dynamic mesh to garage floor label (stored in foundationsPrefixValues)
  if (fieldName === 'meshToGarageFloor' && prefixValues?.meshToGarageFloor) {
    return `Mesh to Garage Floor ${prefixValues.meshToGarageFloor}`;
  }

  // Handle dynamic pileMat label — replace 'xxxmm' with the actual thickness from prefixValues.
  // Decimal entries are converted to mm for display via `formatDimensionLabel`.
  if (fieldName === 'pileMat' && prefixValues?.pileMat) {
    const raw = formatDimensionLabel(prefixValues.pileMat);
    const withUnit = raw === '' ? raw : raw.toLowerCase().endsWith('mm') ? raw : `${raw}mm`;
    return (FOUNDATIONS_FIELD_LABELS['pileMat'] ?? '').replace(/xxxmm/gi, withUnit);
  }

  // Handle dynamic concreteBlinding label — replace 'xxmm' with the actual thickness from prefixValues.
  // Decimal entries are converted to mm for display via `formatDimensionLabel`.
  if (fieldName === 'concreteBlinding' && prefixValues?.concreteBlinding) {
    const raw = formatDimensionLabel(prefixValues.concreteBlinding);
    const withUnit = raw === '' ? raw : raw.toLowerCase().endsWith('mm') ? raw : `${raw}mm`;
    return (FOUNDATIONS_FIELD_LABELS['concreteBlinding'] ?? '').replace(/xxmm/gi, withUnit);
  }

  // Handle dynamic B&B floor house label
  if (fieldName === 'bbFloorHouse' && prefixValues?.bbFloorHouse) {
    const evalCode = prefixValues.bbFloorHouse as EvaluateCode;
    return EVALUATE_CODES[evalCode]?.label ?? FOUNDATIONS_FIELD_LABELS[fieldName];
  }

  // Handle dynamic B&B floor garage label (GWALPF006 / GWALPF012 picked from
  // the Configure modal Floors→Garage selector).
  if (fieldName === 'bbFloorGarage' && prefixValues?.bbFloorGarage) {
    const evalCode = prefixValues.bbFloorGarage as EvaluateCode;
    return EVALUATE_CODES[evalCode]?.label ?? FOUNDATIONS_FIELD_LABELS[fieldName];
  }

  // Handle dynamic concrete type labels
  const concreteTypeFields = ['gen1ConcreteToStrip', 'rc35ConcreteToPiled', 'rc35ConcreteToVibro', 'gen1AdditionalConcrete'];
  if (concreteTypeFields.includes(fieldName) && prefixValues && prefixValues[fieldName]) {
    const baseLabel = FOUNDATIONS_FIELD_LABELS[fieldName];
    return baseLabel?.replace(/xx/gi, prefixValues[fieldName]) || null;
  }

  // List of substructure fields that need block type appended
  const substructureFields = [
    'trench4NBlock300mmWide', 'trench7NBlock300mmWide', 'trench4NBlock355mmWide',
    'trench7NBlock355mmWide', 'dense4NBlockwork100mm', 'dense7NBlockwork100mm',
    'dense10NBlockwork100mm', 'aerated4NBlockwork100mm', 'aerated7NBlockwork100mm',
    'aerated4NBlockwork140mm', 'aerated7NBlockwork140mm', 'dense100mmCoursingBrick',
    'aerated100mmCoursingBrick', 'aerated140mmCoursingBrick'
  ];

  const baseLabel = FOUNDATIONS_FIELD_LABELS[fieldName];

  // Handle dynamic substructure block type label
  if (substructureFields.includes(fieldName) && substructureBlockType && baseLabel) {
    return `${baseLabel} ${substructureBlockType}`;
  }

  return baseLabel || null;
}

/**
 * Get the unit for a Foundations field
 */
export function getFoundationsFieldUnit(fieldName: string): string | null {
  return FOUNDATIONS_FIELD_UNITS[fieldName] || null;
}
